import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
	Box,
	Grid,
	Skeleton,
	Tab,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroLink,
	CerebroPageContainer,
	CerebroText,
} from '../../shared/components';
import { useLoanApplicationQuery } from '../../shared/hooks';
import { Deal, DealPreferenceItem } from '../../shared/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fa1, fa2, fa3, fa4, fa5 } from '@fortawesome/pro-light-svg-icons';
import { FinancialInformation } from './FinancialInformation';
import { useAuth } from '../../shared/context';
import { RequestInformation } from './RequestInformation';
import { SourcesUses } from './SourcesUses';
import { DebtSchedule } from './DebtSchedule';
import { PlatformRootUrl } from '../../shared/utils/platform';
import { DateUtils } from '../../shared/utils';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { GetBusinessSalesTypeDescription } from '../../shared/utils/enum';

export const LoanApplication: React.FC = () => {
	const { guid } = useParams<'guid'>();
	const [tab, setTab] = React.useState('settings');
	const { currentUser } = useAuth();

	const loanApplicationQuery = useLoanApplicationQuery(guid);

	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const showBorrower = loanApplicationQuery.data?.showBorrowerMetrics ?? false;
	const showAcquisition = loanApplicationQuery.data?.showAcquisitionMetrics ?? false;
	const dealPreferences = getDealPreferenceItems(loanApplicationQuery.data?.deal);
	const showb2Percentages =
		loanApplicationQuery.data != null &&
		(loanApplicationQuery.data.deal.b2bPercent != null ||
			loanApplicationQuery.data.deal.b2cPercent != null ||
			loanApplicationQuery.data.deal.b2gPercent != null);
	const hasb2bPercentage =
		loanApplicationQuery.data != null &&
		loanApplicationQuery.data.deal.b2bPercent != null &&
		loanApplicationQuery.data.deal.b2bPercent >= 0;
	const hasb2cPercentage =
		loanApplicationQuery.data != null &&
		loanApplicationQuery.data.deal.b2cPercent != null &&
		loanApplicationQuery.data.deal.b2cPercent >= 0;
	const hasb2gPercentage =
		loanApplicationQuery.data != null &&
		loanApplicationQuery.data.deal.b2gPercent != null &&
		loanApplicationQuery.data.deal.b2gPercent >= 0;
	const showAcquisitionB2Percentages =
		loanApplicationQuery.data != null &&
		(loanApplicationQuery.data.deal.acquisitionB2bPercent != null ||
			loanApplicationQuery.data.deal.acquisitionB2cPercent != null ||
			loanApplicationQuery.data.deal.acquisitionB2gPercent != null);
	const hasAcquisitionB2bPercentage =
		loanApplicationQuery.data != null &&
		loanApplicationQuery.data.deal.acquisitionB2bPercent != null &&
		loanApplicationQuery.data.deal.acquisitionB2bPercent >= 0;
	const hasAcquisitionB2cPercentage =
		loanApplicationQuery.data != null &&
		loanApplicationQuery.data.deal.acquisitionB2cPercent != null &&
		loanApplicationQuery.data.deal.acquisitionB2cPercent >= 0;
	const hasAcquisitionB2gPercentage =
		loanApplicationQuery.data != null &&
		loanApplicationQuery.data.deal.acquisitionB2gPercent != null &&
		loanApplicationQuery.data.deal.acquisitionB2gPercent >= 0;

	return (
		<CerebroPageContainer maxWidth="100%">
			<CerebroBreadcrumb>Loan Application</CerebroBreadcrumb>

			{!currentUser.isBankUser && !currentUser.isCompanyUser && loanApplicationQuery.isSuccess && (
				<CerebroLink
					sx={{ display: 'block', marginBottom: 2 }}
					href={getEditUrl(loanApplicationQuery.data.isRawLead, loanApplicationQuery.data.dealGuid)}
					variant="large"
					target="_blank"
				>
					<FontAwesomeIcon icon={faEdit} />
					Edit
				</CerebroLink>
			)}

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Settings" value="settings" />
						<Tab label="Sources & Uses" value="sources" />
						<Tab label="Request" value="request" />
						<Tab label="Debt Schedule" value="debt" />
						<Tab label="Company" value="company" />
						<Tab label="Financials" value="financials" />
						{!currentUser.isBankUser &&
							loanApplicationQuery.data?.excludedLenders &&
							loanApplicationQuery.data.excludedLenders.length > 0 && (
								<Tab label="Excluded Lenders" value="excluded" />
							)}
						{!currentUser.isBankUser &&
							loanApplicationQuery.data?.reducedFeeLenders &&
							loanApplicationQuery.data.reducedFeeLenders.length > 0 && (
								<Tab label="Reduced Fee Lenders" value="reducedFee" />
							)}
					</TabList>
				</Box>

				<TabPanel value="settings">
					<Grid container spacing={2} sx={{ marginBottom: 2 }}>
						<ApplicatonGridDisplayRow
							label="Deal/Project Name"
							text={loanApplicationQuery.data?.deal.dealName}
						/>

						{!!loanApplicationQuery.data?.transactionManager && (
							<ApplicatonGridDisplayRow
								label="Transaction Manager"
								text={loanApplicationQuery.data?.transactionManager}
							/>
						)}

						{!!loanApplicationQuery.data?.secondaryTransactionManager && (
							<ApplicatonGridDisplayRow
								label="Secondary Transaction Manager"
								text={loanApplicationQuery.data?.secondaryTransactionManager}
							/>
						)}

						{!!loanApplicationQuery.data?.lenderRelationsManager && (
							<ApplicatonGridDisplayRow
								label="Lender Relations Manager"
								text={loanApplicationQuery.data?.lenderRelationsManager}
							/>
						)}

						{loanApplicationQuery.data?.deal.isAsapTargetDate ? (
							<ApplicatonGridDisplayRow label="Target Funding Date" text={'ASAP'} />
						) : (
							<ApplicatonGridDisplayRow
								label="Target Funding Date"
								text={DateUtils.FormatStringAsShortDate(
									loanApplicationQuery.data?.deal.targetFundingDate?.toString()
								)}
							/>
						)}
						<Grid item xs={1}></Grid>
						<Grid item xs={7} md={4}>
							<CerebroText sx={{ fontWeight: 'bold' }}>Deal Preferences</CerebroText>
						</Grid>
						<Grid item xs={4} md={7} alignSelf="flex-end">
							{dealPreferences.map((preference, index) => {
								return (
									<CerebroText key={preference.propertyName}>
										{getPreferenceIcon(index)} &nbsp; {preference.displayName}
									</CerebroText>
								);
							})}
						</Grid>
					</Grid>
				</TabPanel>
				<TabPanel value="sources">
					<SourcesUses
						isLoading={loanApplicationQuery.isLoading}
						sources={loanApplicationQuery.data?.sources}
						uses={loanApplicationQuery.data?.uses}
					/>
				</TabPanel>
				<TabPanel value="request">
					<RequestInformation
						isLoading={loanApplicationQuery.isLoading}
						allocation={loanApplicationQuery.data?.allocation}
					/>
				</TabPanel>
				<TabPanel value="debt">
					<DebtSchedule
						isLoading={loanApplicationQuery.isLoading}
						debtSchedules={loanApplicationQuery.data?.debtSchedules}
					/>
				</TabPanel>
				<TabPanel value="company">
					{loanApplicationQuery.isLoading && (
						<>
							<Skeleton height={60} />
							<Skeleton height={60} />
							<Skeleton height={60} />
						</>
					)}

					{loanApplicationQuery.isSuccess && showBorrower && (
						<Grid container spacing={2} sx={{ marginBottom: 2 }}>
							{showAcquisition && (
								<Grid item xs={12}>
									<CerebroHeading size="h2" component="h2" sx={{ fontWeight: 'bold' }}>
										Company
									</CerebroHeading>
								</Grid>
							)}
							<ApplicatonGridDisplayRow
								label="What is the company's name?"
								text={loanApplicationQuery.data?.deal.companyName}
							/>
							<ApplicatonGridDisplayRow
								label="What is the company's industry?"
								text={loanApplicationQuery.data?.deal.companyIndustry?.name}
							/>
							<ApplicatonGridDisplayRow
								label="Which state is the company's located?"
								text={loanApplicationQuery.data?.deal.state?.name}
							/>
							<ApplicatonGridDisplayRow
								label="How many years has the company been operating?"
								text={loanApplicationQuery.data?.deal.yearsInOperation?.toString()}
							/>
							<ApplicatonGridDisplayRow
								label="How many US employees does the company employ?"
								text={loanApplicationQuery.data?.deal.numberEmployees?.toString()}
							/>
							<ApplicatonGridDisplayRow
								label="How many Non-US employees does the company employ?"
								text={loanApplicationQuery.data?.deal.nonUsBasedEmployees?.toString()}
							/>
							<ApplicatonGridDisplayRow
								label="Is the company backed by an institutional investor?"
								text={loanApplicationQuery.data?.deal.institutionalInvestor ? 'Yes' : 'No'}
							/>
							<Grid item xs={1}></Grid>
							<Grid item xs={7} md={4}>
								<CerebroText sx={{ fontWeight: 'bold' }}>
									What percentage of your business comes from each of the following?
								</CerebroText>
							</Grid>
							<Grid item xs={4} md={7} alignSelf="flex-end">
								{showb2Percentages && (
									<>
										{hasb2bPercentage && (
											<CerebroText>
												B2B: {loanApplicationQuery.data?.deal.b2bPercent}%
											</CerebroText>
										)}
										{hasb2cPercentage && (
											<CerebroText>
												B2C: {loanApplicationQuery.data?.deal.b2cPercent}%
											</CerebroText>
										)}
										{hasb2gPercentage && (
											<CerebroText>
												B2G: {loanApplicationQuery.data?.deal.b2gPercent}%
											</CerebroText>
										)}
									</>
								)}
								{!showb2Percentages && <>Not Selected</>}
							</Grid>
							<br />
							&nbsp;
						</Grid>
					)}

					{loanApplicationQuery.isSuccess && showAcquisition && (
						<Grid container spacing={2} sx={{ marginBottom: 2 }}>
							<Grid item xs={12}>
								<CerebroHeading size="h2" component="h2" sx={{ fontWeight: 'bold' }}>
									Company - Acquisition Target
								</CerebroHeading>
							</Grid>

							<ApplicatonGridDisplayRow
								label="What is the company's name?"
								text={loanApplicationQuery.data?.deal.companyName}
							/>
							<ApplicatonGridDisplayRow
								label="What is the company's industry?"
								text={loanApplicationQuery.data?.deal.acquisitionIndustry?.name}
							/>
							<ApplicatonGridDisplayRow
								label="Which state is the company's located?"
								text={loanApplicationQuery.data?.deal.acquisitionState?.name}
							/>
							<ApplicatonGridDisplayRow
								label="How many years has the company been operating?"
								text={loanApplicationQuery.data?.deal.acquisitionYearsInOperation?.toString()}
							/>
							<ApplicatonGridDisplayRow
								label="How many US employees does the company employ?"
								text={loanApplicationQuery.data?.deal.acquisitionUsBasedEmployees?.toString()}
							/>
							<ApplicatonGridDisplayRow
								label="How many Non-US employees does the company employ?"
								text={loanApplicationQuery.data?.deal.acquisitionNonUsBasedEmployees?.toString()}
							/>
							<ApplicatonGridDisplayRow
								label="Is the company backed by an institutional investor?"
								text={loanApplicationQuery.data?.deal.acquisitionInstitutionalInvestor ? 'Yes' : 'No'}
							/>
							<ApplicatonGridDisplayRow
								label="Which type of sales account for the majority of your revenues?"
								text={GetBusinessSalesTypeDescription(
									loanApplicationQuery.data?.deal.acquisitionBusinessSalesType
								)}
							/>
							<Grid item xs={1}></Grid>
							<Grid item xs={7} md={4}>
								<CerebroText sx={{ fontWeight: 'bold' }}>
									What percentage of your business comes from each of the following?
								</CerebroText>
							</Grid>
							<Grid item xs={4} md={7} alignSelf="flex-end">
								{showAcquisitionB2Percentages && (
									<>
										{hasAcquisitionB2bPercentage && (
											<CerebroText>
												B2B: {loanApplicationQuery.data?.deal.acquisitionB2bPercent}%
											</CerebroText>
										)}
										{hasAcquisitionB2cPercentage && (
											<CerebroText>
												B2C: {loanApplicationQuery.data?.deal.acquisitionB2cPercent}%
											</CerebroText>
										)}
										{hasAcquisitionB2gPercentage && (
											<CerebroText>
												B2G: {loanApplicationQuery.data?.deal.acquisitionB2gPercent}%
											</CerebroText>
										)}
									</>
								)}
								{!showAcquisitionB2Percentages && <>Not Selected</>}
							</Grid>
						</Grid>
					)}
				</TabPanel>
				<TabPanel value="financials">
					{loanApplicationQuery.isLoading && loanApplicationQuery.isLoading && (
						<>
							<Skeleton height={60} />
							<Skeleton height={60} />
							<Skeleton height={60} />
						</>
					)}

					{loanApplicationQuery.isSuccess && loanApplicationQuery.isSuccess && showBorrower && (
						<FinancialInformation
							isLoading={loanApplicationQuery.isLoading}
							deal={loanApplicationQuery.data.deal}
							financialMetrics={loanApplicationQuery.data.financialMetrics}
							analysisType="Borrower"
							showHeading={showAcquisition}
						/>
					)}

					{loanApplicationQuery.isSuccess && loanApplicationQuery.isSuccess && showAcquisition && (
						<FinancialInformation
							isLoading={loanApplicationQuery.isLoading}
							deal={loanApplicationQuery.data.deal}
							financialMetrics={loanApplicationQuery.data.financialMetrics}
							analysisType="Acquisition"
							showHeading={true}
						/>
					)}
				</TabPanel>
				{!currentUser.isBankUser &&
					loanApplicationQuery.data?.excludedLenders &&
					loanApplicationQuery.data.excludedLenders.length > 0 && (
						<TabPanel value="excluded">
							{loanApplicationQuery.isLoading && (
								<>
									<Skeleton height={60} />
									<Skeleton height={60} />
									<Skeleton height={60} />
								</>
							)}

							<TableContainer>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Lender</TableCell>
											<TableCell>Incumbent</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{loanApplicationQuery.data?.excludedLenders.map(lender => (
											<TableRow key={lender.guid}>
												<TableCell>{lender.bankName}</TableCell>
												<TableCell>{lender.isIncumbentLender ? 'Yes' : 'No'}</TableCell>
											</TableRow>
										))}
										{loanApplicationQuery.data?.excludedLenders.length == 0 && (
											<TableRow>
												<TableCell colSpan={2}>
													<CerebroText>
														<br />
														No providers have been excluded from this deal.
														<br />
														&nbsp;
													</CerebroText>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</TabPanel>
					)}
				{!currentUser.isBankUser &&
					loanApplicationQuery.data?.reducedFeeLenders &&
					loanApplicationQuery.data.reducedFeeLenders.length > 0 && (
						<TabPanel value="reducedFee">
							{loanApplicationQuery.isLoading && (
								<>
									<Skeleton height={60} />
									<Skeleton height={60} />
									<Skeleton height={60} />
								</>
							)}

							<TableContainer>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Lender</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{loanApplicationQuery.data?.reducedFeeLenders.map(lender => (
											<TableRow key={lender.guid}>
												<TableCell>{lender.bankName}</TableCell>
											</TableRow>
										))}
										{loanApplicationQuery.data?.reducedFeeLenders.length == 0 && (
											<TableRow>
												<TableCell>
													<CerebroText>
														<br />
														No providers have been marked as reduced fee for this deal.
														<br />
														&nbsp;
													</CerebroText>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</TabPanel>
					)}
			</TabContext>
		</CerebroPageContainer>
	);
};

interface ApplicatonGridDisplayRowProps {
	label: string;
	text: string | undefined | null;
}
const ApplicatonGridDisplayRow: React.FC<ApplicatonGridDisplayRowProps> = ({ label, text }) => {
	return (
		<>
			<Grid item xs={1}></Grid>
			<Grid item xs={7} md={4}>
				<CerebroText sx={{ fontWeight: 'bold' }}>{label}</CerebroText>
			</Grid>
			<Grid item xs={4} md={7} alignSelf="flex-end">
				{text}
			</Grid>
		</>
	);
};

export function getEditUrl(isRawLead: boolean, dealGuid: string): string {
	if (isRawLead) {
		return `${PlatformRootUrl}/Emp/RawLeadData?id=${dealGuid}`;
	}

	return `${PlatformRootUrl}/Emp/LoanApplication/${dealGuid}`;
}

function getPriorityTextFromValue(value?: number | null) {
	switch (value) {
		case 0:
			return 'Very Important';
		case 1:
			return 'Important';
		case 2:
			return 'Neutral';
		case 3:
			return 'Not Important';
		case 4:
			return 'Marginal';
		default:
			return 'NA';
	}
}

function getPriorityValueFromText(text: string) {
	switch (text) {
		case 'Very Important':
			return 0;
		case 'Important':
			return 1;
		case 'Neutral':
			return 2;
		case 'Not Important':
			return 3;
		case 'Marginal':
			return 4;
		default:
			return 5;
	}
}

function getDealPreferenceItems(value?: Deal) {
	if (value === undefined) return [];
	const items: any[] = [
		{
			displayName: 'Cost of Capital',
			propertyName: 'PricePriority',
			index: getPriorityValueFromText(value.pricePriority),
		},
		{
			displayName: 'Covenant Levels',
			propertyName: 'CovenantPriority',
			index: getPriorityValueFromText(value.covenantPriority),
		},
		{
			displayName: 'Term of Loan',
			propertyName: 'MaturityPriority',
			index: getPriorityValueFromText(value.maturityPriority),
		},
		{
			displayName: 'Amortization',
			propertyName: 'AmortizationPriority',
			index: getPriorityValueFromText(value.amortizationPriority),
		},
		{
			displayName: 'Size of Loan',
			propertyName: 'AmountPriority',
			index: getPriorityValueFromText(value.amountPriority),
		},
	];

	return items
		.sort((a, b) => a.index - b.index)
		.map<DealPreferenceItem>((item, index) => {
			return { propertyName: item.propertyName, displayName: item.displayName, index: index + 1 };
		});
}

function getPreferenceIcon(index: number): JSX.Element {
	switch (index) {
		case 0:
			return <FontAwesomeIcon icon={fa1} />;
		case 1:
			return <FontAwesomeIcon icon={fa2} />;
		case 2:
			return <FontAwesomeIcon icon={fa3} />;
		case 3:
			return <FontAwesomeIcon icon={fa4} />;
		case 4:
			return <FontAwesomeIcon icon={fa5} />;
	}

	return <FontAwesomeIcon icon={fa1} />;
}
